/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Button, Table, SvgIcon, Input, Alert } from "core/components";
import { ProjectFormData, PurchaseRequestStatus } from "core/schema";
import moment from "moment";
import ProjectPurchaseRequest from "./ProjectPurchaseRequest";
import { api, ApiError } from "core/api";
import {
  admin_purchase_request_path,
  approve_purchase_request_admin_project_path,
  delete_purchase_request_admin_project_path,
  print_admin_purchase_request_path,
  reject_purchase_request_admin_project_path
} from "routes";
import { useForm } from "react-hook-form";

type ProjectPurchaseRequestsProps = {
  project: ProjectFormData;
};

const ProjectPurchaseRequests = ({
  project,
  loading,
  purchase_requests,
  getPurchaseRequests,
  can_approve_reject,
  can_delete
}: ProjectPurchaseRequestsProps): JSX.Element => {
  const {
    control,
    formState: { isSubmitting, isValidating },
    handleSubmit,
    reset,
    getValues,
    setValue
  } = useForm({
    mode: "onChange"
  });
  const [purchaseRequests, setPurchaseRequests] = React.useState([]);
  const [purchaseRequest, setPurchaseRequest] = React.useState(null);
  const [showPurchaseRequestModal, setShowPurchaseRequestModal] = React.useState(false);
  const [showApprovePurchaseRequestModal, setShowApprovePurchaseRequestModal] = React.useState(false);
  const [showRejectPurchaseRequestModal, setShowRejectPurchaseRequestModal] = React.useState(false);
  const [showDeletePurchaseRequestModal, setShowDeletePurchaseRequestModal] = React.useState(false);
  const [itemsForDestruction, setItemsForDestruction] = React.useState([]);
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const policy = project.policy;
  const table = React.useRef(null);

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id"
      },
      {
        id: 1,
        Header: "PR No.",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-truncate text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_request.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Created",
        accessor: "created_at",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Date Closed",
        accessor: "date_closed",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value ? moment(props.value).format("MM/DD/YYYY") : "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Requestor",
        accessor: "requested_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Approver",
        accessor: "approved_by",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value || "-"}</p>
            </div>
          );
        }
      },
      {
        Header: "Actions",
        accessor: "",
        Cell: (props) => {
          const isOpen = props.row.values.status == PurchaseRequestStatus.Open;
          const isForApproval = props.row.values.status == PurchaseRequestStatus.ForApproval;
          const hasPurchaseOrders = props.row.original.purchase_request_items?.some((item) => item.purchase_orders?.length > 0);

          return (
            <div className="d-flex text-center">
              <span title="View Purchase Request" className="d-flex" data-bs-toggle="tooltip">
                <a
                  className="button button--action button--action-success"
                  onClick={() => {
                    setPurchaseRequest(props.row.original);
                    setShowPurchaseRequestModal(!showPurchaseRequestModal);
                  }}
                >
                  <SvgIcon name="view" className="h5" />
                </a>
              </span>
              {!isForApproval && (
                <span title="Print Purchase Request" className="d-flex" data-bs-toggle="tooltip">
                  <a
                    className="button button--action button--action-primary"
                    onClick={() => window.open(print_admin_purchase_request_path(props.row.original.id))}
                  >
                    <SvgIcon name="printer" className="h5" />
                  </a>
                </span>
              )}
              {can_approve_reject && isForApproval && (
                <>
                  <span title="Approve Purchase Request" className="d-flex" data-bs-toggle="tooltip">
                    <a
                      className="button button--action button--action-approve"
                      onClick={() => {
                        setPurchaseRequest(props.row.original);
                        setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
                      }}
                    >
                      <SvgIcon name="check-circle" className="h6" />
                    </a>
                  </span>
                  <span title="Reject Purchase Request" className="d-flex" data-bs-toggle="tooltip">
                    <a
                      className="button button--action button--action-danger"
                      onClick={() => {
                        setPurchaseRequest(props.row.original);
                        setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
                      }}
                    >
                      <SvgIcon name="times-circle" className="h6" />
                    </a>
                  </span>
                </>
              )}
              {can_delete && (isOpen || isForApproval) && !hasPurchaseOrders && (
                <span title="Delete Purchase Request" className="d-flex" data-bs-toggle="tooltip">
                  <a
                    className="button button--action button--action-danger"
                    onClick={() => {
                      setPurchaseRequest(props.row.original);
                      setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
                    }}
                  >
                    <SvgIcon name="delete" className="h6" />
                  </a>
                </span>
              )}
            </div>
          );
        }
      }
    ],
    []
  );

  const updatePurchaseRequests = (purchaseRequests, updatedPurchaseRequest) => {
    const updatedPurchaseRequests = purchaseRequests.map((purchaseRequest) => {
      if (purchaseRequest.id == updatedPurchaseRequest.id) {
        return { ...purchaseRequest, ...updatedPurchaseRequest };
      }

      return purchaseRequest;
    });

    setPurchaseRequests(updatedPurchaseRequests);
  };

  const approvePurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId) => {
    try {
      const response = await api.put(approve_purchase_request_admin_project_path({ id: project.id }, { purchase_request_id: purchaseRequestId }));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const rejectPurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId) => {
    try {
      const response = await api.put(reject_purchase_request_admin_project_path({ id: project.id }, { purchase_request_id: purchaseRequestId }));
      updatePurchaseRequests(purchaseRequests, response.data);
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const deletePurchaseRequest = React.useCallback(async (purchaseRequests, purchaseRequestId) => {
    try {
      const response = await api.deleteItem(
        delete_purchase_request_admin_project_path({ id: project.id }, { purchase_request_id: purchaseRequestId })
      );
      setPurchaseRequests(purchaseRequests.filter((purchaseRequest) => purchaseRequest.id != response.data.id));
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  }, []);

  const withItemsForDestruction = (data) => {
    const { purchase_request_items } = data;

    return { ...data, purchase_request_items: [...purchase_request_items, ...itemsForDestruction] };
  };

  const save = async (data): Promise<unknown> => {
    try {
      await api.put(admin_purchase_request_path(data.id), {
        ...withItemsForDestruction(data)
      });
      setPurchaseRequests(await getPurchaseRequests());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    } finally {
      setShowPurchaseRequestModal(!showPurchaseRequestModal);
    }
  };

  const onError = (data) => {
    console.error(data);
  };

  React.useEffect(() => {
    setPurchaseRequests(purchase_requests);
  }, [JSON.stringify(purchase_requests)]);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Project Purchase Requests...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="clipboard-list" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No purchase requests.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Request Number" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table columns={columns} data={purchaseRequests} ref={table} />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return purchaseRequests.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  const canUpdate = (purchaseRequest) => {
    return (
      [PurchaseRequestStatus.Open, PurchaseRequestStatus.ForApproval].includes(purchaseRequest?.status) &&
      purchaseRequest?.purchase_request_items?.some((item) => !(item.purchase_orders?.length > 0)) &&
      policy.can_save
    );
  };

  return (
    <React.StrictMode>
      <Col>
        {formErrors && formErrors.length != 0 && (
          <Alert>
            {formErrors.map((error, index) => {
              return <div key={index}>{error}</div>;
            })}
          </Alert>
        )}
      </Col>
      {loading ? loader() : content()}
      <Modal dialogClassName="modal-dialog-centered modal-xl" show={showPurchaseRequestModal}>
        <Modal.Header>{`Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          <ProjectPurchaseRequest
            purchaseRequest={purchaseRequest}
            control={control}
            reset={reset}
            getValues={getValues}
            setValue={setValue}
            setItemsForDestruction={setItemsForDestruction}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseRequestModal(!showPurchaseRequestModal);
            }}
            icon="times"
            title="Close"
          />
          {canUpdate(purchaseRequest) && (
            <Button
              className="button--brand"
              icon="save"
              title="Save"
              disabled={isSubmitting || isValidating}
              onClick={() => {
                handleSubmit(async (data) => await save(data), onError)();
              }}
            />
          )}
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showApprovePurchaseRequestModal}>
        <Modal.Header>{`Approve Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to approve purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--brand"
            icon="save"
            title="Yes, approve purchase request."
            onClick={() => {
              approvePurchaseRequest(purchaseRequests, purchaseRequest.id).then(() => {
                setShowApprovePurchaseRequestModal(!showApprovePurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showRejectPurchaseRequestModal}>
        <Modal.Header>{`Reject Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to reject purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="times-circle"
            title="Yes, reject purchase request."
            onClick={() => {
              rejectPurchaseRequest(purchaseRequests, purchaseRequest.id).then(() => {
                setShowRejectPurchaseRequestModal(!showRejectPurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
      <Modal dialogClassName="modal-dialog-centered" show={showDeletePurchaseRequestModal}>
        <Modal.Header>{`Delete Purchase Request: ${purchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>{`Are you sure you want to delete purchase request: ${purchaseRequest?.code}?`}</Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
            }}
            icon="times"
            title="Cancel"
          />
          <Button
            className="button--danger"
            icon="delete"
            title="Yes, delete purchase request."
            onClick={() => {
              deletePurchaseRequest(purchaseRequests, purchaseRequest.id).then(() => {
                setShowDeletePurchaseRequestModal(!showDeletePurchaseRequestModal);
              });
            }}
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default ProjectPurchaseRequests;
